/**
 * @file api.ts
 * @description Typed fetch wrapper for the API. Holds the access token in
 *   memory, attaches it as `Authorization: Bearer`, and on a 401 attempts
 *   one refresh (using the __Host-rt cookie sent automatically). Other
 *   errors are surfaced as ApiError with the RFC 7807 fields.
 * @module Lib
 * @security The access token lives in memory only (never localStorage).
 *   Refresh-token cookie is HttpOnly+Secure+SameSite=Strict — issued by
 *   the API on login/register/refresh, transparent to JS.
 */

export interface ApiProblem {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  request_id?: string;
  errors?: Array<{ field?: string; code?: string; message: string }>;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly problem: ApiProblem;
  public constructor(problem: ApiProblem) {
    super(problem.detail);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }
}

type TokenListener = (token: string | null) => void;
type SessionExpiredListener = () => void;

let accessToken: string | null = null;
// True once we've ever held an active access token. Read by the
// session-expired signal logic to distinguish "session ended mid-use"
// from "bootstrap with no prior session". Declared next to accessToken
// so setAccessToken below can safely flip it.
let hadActiveSession = false;
const tokenListeners = new Set<TokenListener>();
const sessionExpiredListeners = new Set<SessionExpiredListener>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token !== null) hadActiveSession = true;
  for (const listener of tokenListeners) listener(token);
}

export function onTokenChange(listener: TokenListener): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

/**
 * Fired only when a mid-session refresh fails — i.e. the user HAD a
 * valid access token (or the cookie implied one) and then we couldn't
 * renew it. NOT fired on a clean bootstrap with no prior session, so
 * a fresh visitor to /login doesn't see a "session expired" banner.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

function emitSessionExpired(): void {
  for (const listener of sessionExpiredListeners) listener();
}

interface RequestInitJson extends Omit<RequestInit, 'body'> {
  json?: unknown;
}

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight !== null) return refreshInFlight;
  const hadTokenAtStart = accessToken !== null;
  refreshInFlight = (async () => {
    const res = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!res.ok) {
      setAccessToken(null);
      if (hadTokenAtStart || hadActiveSession) emitSessionExpired();
      return false;
    }
    const data = (await res.json()) as { access_token: string };
    setAccessToken(data.access_token);
    hadActiveSession = true;
    return true;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

async function rawFetch(path: string, init: RequestInitJson): Promise<Response> {
  const { json, ...requestInit } = init;
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (json !== undefined) headers.set('Content-Type', 'application/json');
  if (accessToken !== null && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  const body = json !== undefined ? JSON.stringify(json) : undefined;
  return fetch(`/api/v1${path}`, {
    ...requestInit,
    headers,
    body,
    credentials: 'include',
  });
}

async function rawAuthenticatedFetch(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  if (accessToken !== null && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return fetch(`/api/v1${path}`, { ...init, headers, credentials: 'include' });
}

/**
 * Make an authenticated non-JSON request. Used for multipart uploads and
 * binary downloads that cannot use api(). On 401, refreshes the access
 * token once and retries with the original body.
 */
export async function authenticatedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  let res = await rawAuthenticatedFetch(path, init);
  if (res.status === 401 && path !== '/auth/refresh' && path !== '/auth/login') {
    const refreshed = await tryRefresh();
    if (refreshed) res = await rawAuthenticatedFetch(path, init);
  }
  return res;
}

/**
 * Make an authenticated API call. On 401 attempts one refresh and retries.
 * Throws ApiError for any non-2xx response.
 */
export async function api<T>(path: string, init: RequestInitJson = {}): Promise<T> {
  let res = await rawFetch(path, init);
  if (res.status === 401 && path !== '/auth/refresh' && path !== '/auth/login') {
    const refreshed = await tryRefresh();
    if (refreshed) res = await rawFetch(path, init);
  }
  if (!res.ok) {
    const problem = (await res.json().catch(() => fallbackProblem(res))) as ApiProblem;
    throw new ApiError(problem);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function fallbackProblem(res: Response): ApiProblem {
  return {
    type: 'about:blank',
    title: res.statusText,
    status: res.status,
    detail: `Unexpected response (${res.status})`,
    instance: '',
  };
}
